import type { Fixture, FixtureSide } from './api';

/**
 * Who won a season, read off its Final.
 *
 * Only a FINISHED Final crowns anybody: completed, with a result. A Final
 * saved part-way — pairs named, two matches in — is a result row too, and a
 * champions banner on a tie still being played would be announcing a winner
 * the hall has not seen yet.
 *
 * A Final that finished LEVEL is settled by its decider (match_deciders), which
 * the fixture list does not carry. So this says so — `needsDecider` — and the
 * caller asks the match for it, rather than crowning whoever is listed first.
 */
export interface FinalOutcome {
  fixture: Fixture;
  champion: FixtureSide;
  runnerUp: FixtureSide;
  /** The champion's points first, the way somebody would say the score. */
  score: { champion: number; runnerUp: number };
}

export type FinalReading =
  | { kind: 'none' }
  | { kind: 'needsDecider'; fixture: Fixture }
  | { kind: 'decided'; outcome: FinalOutcome };

export function readFinal(fixtures: Fixture[], deciderWonBy?: 'home' | 'away' | null): FinalReading {
  const final = fixtures.find((f) => f.stage === 'final');
  if (!final || final.status !== 'completed' || !final.result) return { kind: 'none' };

  const home = final.result.homePoints;
  const away = final.result.awayPoints;
  if (home === null || away === null) return { kind: 'none' };
  let won: 'home' | 'away' | null = home > away ? 'home' : away > home ? 'away' : null;
  if (won === null) {
    if (!deciderWonBy) return { kind: 'needsDecider', fixture: final };
    won = deciderWonBy;
  }

  const champion = won === 'home' ? final.home : final.away;
  const runnerUp = won === 'home' ? final.away : final.home;
  return {
    kind: 'decided',
    outcome: {
      fixture: final,
      champion,
      runnerUp,
      score: {
        champion: won === 'home' ? home : away,
        runnerUp: won === 'home' ? away : home,
      },
    },
  };
}
